import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wifi, Plus, Zap } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { listInternetForSchool, issueInternetBundle } from "@/lib/internet.functions";
import { fmtNum } from "@/lib/format";

export function InternetAdmin({ schoolId }: { schoolId: string }) {
  const qc = useQueryClient();
  const list = useServerFn(listInternetForSchool);
  const { data: entitlements = [] } = useQuery<any[]>({
    queryKey: ["internet", schoolId], queryFn: () => list({ data: { schoolId } }),
  });
  const { data: students = [] } = useQuery<any[]>({
    queryKey: ["students-simple", schoolId],
    queryFn: async () => (await supabase.from("students").select("id,full_name,class_label").eq("school_id", schoolId).order("full_name")).data ?? [],
  });

  const [open, setOpen] = useState(false);

  const active = entitlements.filter(e => e.status === "active").length;
  const totalMb = entitlements.reduce((s, e) => s + (e.bundle_mb ?? 0), 0);
  const usedMb = entitlements.reduce((s, e) => s + (e.used_mb ?? 0), 0);

  return (
    <div className="space-y-4">
      <Card className="relative overflow-hidden bg-gradient-to-br from-sky-900 via-blue-900 to-indigo-950 p-6 text-sky-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-sky-200/70">Internet & digital services</div>
            <div className="mt-1 font-display text-2xl">Wi-Fi bundles, e-library, lab bookings</div>
            <div className="mt-1 text-sm text-sky-100/80">Metered access tied to student identity. Parents can approve top-ups; caps enforce on the router.</div>
          </div>
          <Button variant="secondary" className="bg-sky-50 text-sky-900 hover:bg-sky-100" onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" />Issue bundle</Button>
        </div>
      </Card>

      <div className="grid gap-3 md:grid-cols-4">
        <MetricCard label="Active bundles" value={`${active}`} icon={Wifi} />
        <MetricCard label="Allocated MB" value={fmtNum(totalMb)} icon={Zap} />
        <MetricCard label="Used MB" value={fmtNum(usedMb)} />
        <MetricCard label="Utilization" value={`${totalMb ? Math.round(usedMb / totalMb * 100) : 0}%`} />
      </div>

      <Card className="p-3">
        <div className="mb-2 font-display text-base">Student entitlements</div>
        <div className="space-y-2">
          {entitlements.map((e: any) => {
            const pct = e.bundle_mb ? Math.min(100, (e.used_mb / e.bundle_mb) * 100) : 0;
            return (
              <div key={e.id} className="rounded-md border p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="grid h-8 w-8 place-items-center rounded-full text-xs font-semibold text-white" style={{ background: e.students?.color_hex ?? "#0ea5e9" }}>{e.students?.initials ?? "?"}</div>
                    <div>
                      <div className="text-sm font-medium">{e.students?.full_name}</div>
                      <div className="text-xs text-muted-foreground">{e.students?.class_label} · {e.bundle_label}</div>
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="font-mono">{fmtNum(e.used_mb)} / {fmtNum(e.bundle_mb)} MB</div>
                    <Badge variant={e.status === "active" ? "default" : "outline"} className="capitalize">{e.status}</Badge>
                  </div>
                </div>
                <Progress value={pct} className="mt-2 h-2" />
              </div>
            );
          })}
          {!entitlements.length && <div className="p-6 text-center text-sm text-muted-foreground">No bundles issued yet</div>}
        </div>
      </Card>

      <IssueDialog open={open} onClose={() => setOpen(false)} schoolId={schoolId} students={students} onDone={() => qc.invalidateQueries({ queryKey: ["internet", schoolId] })} />
    </div>
  );
}

function MetricCard({ label, value, icon: Icon }: { label: string; value: string; icon?: any }) {
  return (
    <Card className="p-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
      </div>
      <div className="mt-1 font-display text-xl">{value}</div>
    </Card>
  );
}

function IssueDialog({ open, onClose, schoolId, students, onDone }: { open: boolean; onClose: () => void; schoolId: string; students: any[]; onDone: () => void }) {
  const [studentId, setStudent] = useState("");
  const [label, setLabel] = useState("Study 2GB · 7 days");
  const [mb, setMb] = useState(2048);
  const [days, setDays] = useState(7);
  const issue = useMutation({
    mutationFn: useServerFn(issueInternetBundle),
    onSuccess: () => { toast.success("Bundle issued"); onDone(); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Issue internet bundle</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          <div><Label>Student</Label>
            <Select value={studentId} onValueChange={setStudent}>
              <SelectTrigger><SelectValue placeholder="Pick student" /></SelectTrigger>
              <SelectContent>{students.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.class_label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Bundle label</Label><Input value={label} onChange={e => setLabel(e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>MB</Label><Input type="number" value={mb} onChange={e => setMb(Number(e.target.value))} /></div>
            <div><Label>Minutes</Label><Input type="number" defaultValue={0} /></div>
            <div><Label>Days</Label><Input type="number" value={days} onChange={e => setDays(Number(e.target.value))} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button disabled={!studentId || issue.isPending} onClick={() => issue.mutate({ data: { schoolId, studentId, bundleLabel: label, bundleMb: mb, minutes: 0, days } })}>Issue</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
